import { _DICC_ALUMNO } from "../../data/modulo/Alumno";
import { _DICC_ESCUELA } from "../../data/modulo/Escuela";
import { _DICC_GRUPO } from "../../data/modulo/Grupo";
import { Router } from "../../routes/Router";
import { TRoutePath } from "../../routes/Routes";
import "../../ui/components-lit/GlobalSearch/GlobalSearch";
import { GlobalSearchComponent } from "../../ui/components-lit/GlobalSearch/GlobalSearch";
import { SearchProvider, SearchResult } from "../../ui/components-lit/GlobalSearch/types";
import { KCardAlumnos } from "../../ui/ventana/AlumnosPanelV2";
import { KGruposCard } from "../../ui/ventana/GruposPanelV2";

const GLOBAL_SEARCH_ID = "global-search-root";
const removeDiacritics = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, '');

/**
 * Maneja la lógica de búsqueda global en la aplicación,
 * proporciona una interfaz para registrar proveedores de búsqueda
 * y realizar búsquedas a través de diferentes módulos como alumnos, escuelas, grupos, etc.
 */
export class GlobalSearchService {
    private static _instance: GlobalSearchService;
    private _component: GlobalSearchComponent | null = null;

    private constructor() { }

    static getInstance(): GlobalSearchService {
        if (!GlobalSearchService._instance) {
            GlobalSearchService._instance = new GlobalSearchService();
        }
        return GlobalSearchService._instance;
    }

    init(): void {
        if (document.body) {
            this.mount();
            return;
        }

        document.addEventListener("DOMContentLoaded", () => this.mount(), { once: true });
    }

    refreshProviders(): void {
        if (!this._component) {
            return;
        }

        this._component.providers = this.buildDefaultProviders();
    }

    private mount(): void {
        if (this._component) {
            return;
        }

        const existing = document.getElementById(GLOBAL_SEARCH_ID);
        if (existing && existing instanceof GlobalSearchComponent) {
            this._component = existing as GlobalSearchComponent;
            this.refreshProviders();
            return;
        }

        const element = new GlobalSearchComponent();
        element.id = GLOBAL_SEARCH_ID;
        element.providers = this.buildDefaultProviders();
        document.body.appendChild(element);
        this._component = element;
    }

    private filter(query: string, idValue: string, searchValues: string[]): boolean {
        const normalizedId = idValue.trim().toLowerCase();
        const normalizedQuery = query.trim().toLowerCase();
        const valuesToSearch = [...searchValues, normalizedId, `0${normalizedId}`, `00${normalizedId}`, `${normalizedId}.0`, `0${normalizedId}.0`, `00${normalizedId}.0`];
        return valuesToSearch.some(value => removeDiacritics(value.toLocaleLowerCase()).includes(normalizedQuery));
    }

    private actionToNavigatePanel(prefixHash: string, id: number | string, mode?: KCardAlumnos | KGruposCard): () => void {
        return () => {
            const modeParam = "All"; // Router._GetCurrentParams().get("mode") || "All";
            const modeFinal = mode || modeParam;
            Router._DoHash(prefixHash as TRoutePath, {
                mode: modeFinal,
                id: id,
            })
        };
    }

    private buildDefaultProviders(): SearchProvider[] {
        const alumnoProvider: SearchProvider = {
            key: "alumno",
            label: "Alumnos",
            search: (query: string): SearchResult[] => {
                const category = "Alumnos";

                return Array.from(_DICC_ALUMNO.values())
                    .filter((alumno) => {
                        const fullName = `${alumno.Nombre} ${alumno.ApPaterno} ${alumno.ApMaterno}`;
                        const idValue = String(alumno.IdChild);
                        return this.filter(query, idValue, [fullName, category]);
                    })
                    .map<SearchResult>((alumno) => ({
                        id: `alumno-${alumno.IdChild}`,
                        title: `${alumno.Nombre} ${alumno.ApPaterno} ${alumno.ApMaterno}`.trim(),
                        subtitle: `ID ${alumno.IdChild}`,
                        category: category,
                        action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild),
                        children: [
                            {
                                id: `alumno-${alumno.IdChild}-datosgenerales`,
                                title: "Información general",
                                category: "Datos",
                                action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild, "palumnoinfogeneral"),
                            },
                            {
                                id: `alumno-${alumno.IdChild}-tutores`,
                                title: "Tutores",
                                category: "Datos",
                                action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild, "palumnotutores"),
                            },
                            {
                                id: `alumno-${alumno.IdChild}-expedientes`,
                                title: "Expedientes",
                                category: "Datos",
                                action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild, "palumnoexpedientes"),
                            },
                            {
                                id: `alumno-${alumno.IdChild}-horariosgrupos`,
                                title: "Horarios y Grupos",
                                category: "Datos",
                                action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild, "palumnohorariosgrupos"),
                            },
                            {
                                id: `alumno-${alumno.IdChild}-finanzas-cargos&descuentos`,
                                title: "Finanzas: cargos y descuentos",
                                category: "Finanzas",
                                action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild, "palumnocargosdescuentos"),
                            },
                            {
                                id: `alumno-${alumno.IdChild}-finanzas-edocta`,
                                title: "Finanzas: estado de cuenta",
                                category: "Finanzas",
                                action: this.actionToNavigatePanel("alumnos/alumnos/panel", alumno.IdChild, "palumnoedocta"),
                            }
                        ]
                    }));
            }
        };

        const escuelaProvider: SearchProvider = {
            key: "escuela",
            label: "Escuelas",
            search: (query: string): SearchResult[] => {
                const category = "Escuelas";

                return Array.from(_DICC_ESCUELA.values())
                    .filter((escuela) => {
                        const schoolName = (escuela.Nombre || "");
                        const idValue = String(escuela.IdKinder);
                        return this.filter(query, idValue, [schoolName, category]);
                    })
                    .map<SearchResult>((escuela) => ({
                        id: `escuela-${escuela.IdKinder}`,
                        title: escuela.Nombre || `Escuela ${escuela.IdKinder}`,
                        subtitle: `ID ${escuela.IdKinder}`,
                        category: category,
                        action: this.actionToNavigatePanel("escuelas/escuelas/panel", escuela.IdKinder),
                    }));
            }
        };

        const grupoProvider: SearchProvider = {
            key: "grupo",
            label: "Grupos",
            search: (query: string): SearchResult[] => {
                const category = "Grupos";

                return Array.from(_DICC_GRUPO.values())
                    .filter((grupo) => {
                        const groupName = (grupo.Nombre || "");
                        const idValue = String(grupo.IdGrupo);
                        return this.filter(query, idValue, [groupName, category]);
                    })
                    .map<SearchResult>((grupo) => ({
                        id: `grupo-${grupo.IdGrupo}`,
                        title: grupo.Nombre || `Grupo ${grupo.IdGrupo}`,
                        subtitle: `ID ${grupo.IdGrupo}`,
                        category: category,
                        action: this.actionToNavigatePanel("escuelas/grupos/panel", grupo.IdGrupo),
                        children: [
                            {
                                id: `grupo-${grupo.IdGrupo}-infogeneral`,
                                title: "Información general",
                                category: "Datos",
                                action: this.actionToNavigatePanel("escuelas/grupos/panel", grupo.IdGrupo, "pgrupoinfogeneral"),
                            },
                            {
                                id: `grupo-${grupo.IdGrupo}-horariooperacion`,
                                title: "Horario de operación",
                                category: "Datos",
                                action: this.actionToNavigatePanel("escuelas/grupos/panel", grupo.IdGrupo, "pgrupohorariooperacion"),
                            },
                            {
                                id: `grupo-${grupo.IdGrupo}-maestros`,
                                title: "Maestros asignados",
                                category: "Datos",
                                action: this.actionToNavigatePanel("escuelas/grupos/panel", grupo.IdGrupo, "pgruposmaestros"),
                            },
                            {
                                id: `grupo-${grupo.IdGrupo}-alumnos`,
                                title: "Alumnos asignados",
                                category: "Datos",
                                action: this.actionToNavigatePanel("escuelas/grupos/panel", grupo.IdGrupo, "pgrupoalumnos"),
                            },
                        ]
                    }));
            }
        };

        return [alumnoProvider, escuelaProvider, grupoProvider];
    }
}

export const globalSearchService = GlobalSearchService.getInstance();